import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { Body, Button, Row, Stack } from '@leanin/ui';

import {
  useCreateBroadcast,
  type CreateBroadcastInput,
} from '@/lib/mutations/admin';
import {
  useCreateGroupBroadcast,
  type CreateGroupBroadcastInput,
} from '@/lib/mutations/groups';
import { useErrorMessage } from '@/lib/error-message';
import { useStepUp, type StepUpOutcome } from '@/lib/use-step-up';
import type { BroadcastDoc } from '@/lib/queries/broadcasts';
import { emptyBroadcastDoc } from './BroadcastBodyEditor';
import {
  BroadcastEditorFields,
  audienceSpecFromState,
  EMPTY_AUDIENCE_STATE,
  type AudienceState,
  type BroadcastAudienceContext,
} from './BroadcastEditorFields';
import {
  PLATFORM_EMAIL_SEND_VIA,
  composeContentFromDraft,
  contentDraftGap,
  contentDraftHasInput,
  newContentDraft,
  type BroadcastContentDraft,
} from './broadcast-content-draft';
import { ScheduleSendButton } from './ScheduleSendButton';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

// One-time prefill, applied on mount. Either variant makes the
// unsaved-changes guard consider the form dirty immediately — deliberate:
// navigating away would lose the prefill.
export type BroadcastComposerInitial =
  // Group mode, from a source post ("Send as broadcast"): seeds the subject
  // and body; `sourcePostId` rides the create request so the sent broadcast
  // links back to the post.
  | {
      readonly kind: 'fromPost';
      readonly subject: string;
      readonly bodyDoc: BroadcastDoc;
      readonly sourcePostId: string;
    }
  // Platform mode, from the /admin/members bulk "Send email" action: seeds
  // the audience (a hand-picked `members` list).
  | { readonly kind: 'audience'; readonly audience: AudienceState };

export interface BroadcastComposerFormProps {
  readonly audience: BroadcastAudienceContext;
  readonly initial?: BroadcastComposerInitial;
}

// `invalid` carries client-side validation copy; `failed` carries the thrown
// API error so each intent resolves it to localized copy with its own
// fallback.
type SubmitOutcome =
  | { readonly status: 'done' }
  | { readonly status: 'cancelled' }
  | { readonly status: 'invalid'; readonly message: string }
  | { readonly status: 'failed'; readonly error: unknown };

// The composer's three intents. `scheduledIso` rides on 'schedule' alone so a
// schedule can't be dispatched without a time and a send/draft can't carry one.
type ComposerAction =
  | { readonly mode: 'send' | 'draft' }
  | { readonly mode: 'schedule'; readonly scheduledIso: string };

export function BroadcastComposerForm({
  audience,
  initial,
}: BroadcastComposerFormProps) {
  const navigate = useNavigate();
  const createPlatform = useCreateBroadcast();
  const createGroup = useCreateGroupBroadcast();
  const { runStepUp } = useStepUp();
  const describeError = useErrorMessage();

  const tDm = useTranslations('Admin.broadcasts.dm');
  const tForm = useTranslations('Admin.broadcasts.form');
  const fromPost = initial?.kind === 'fromPost' ? initial : null;
  const [content, setContent] = useState<BroadcastContentDraft>(() =>
    newContentDraft({
      subject: fromPost?.subject ?? '',
      preheaderText: '',
      bodyDoc: fromPost?.bodyDoc ?? emptyBroadcastDoc(),
      replyTo: '',
    }),
  );
  const [audienceState, setAudienceState] = useState<AudienceState>(
    initial?.kind === 'audience' ? initial.audience : EMPTY_AUDIENCE_STATE,
  );
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [savePending, setSavePending] = useState(false);

  const pending = createPlatform.isPending || createGroup.isPending;

  // Any audience edit (every change yields a new state object) or prefill is
  // a choice the admin would lose by navigating away.
  const dirty =
    contentDraftHasInput(content) ||
    (audience.mode === 'platform' && audienceState !== EMPTY_AUDIENCE_STATE);

  const guard = useUnsavedChangesGuard({ dirty });

  const gap = contentDraftGap(content);
  const minimumFieldsForSave = gap === null;

  const submit = async (action: ComposerAction): Promise<SubmitOutcome> => {
    setError(null);
    if (gap === 'subject') {
      return { status: 'invalid', message: tForm('subjectRequired') };
    }
    if (gap === 'message') {
      return { status: 'invalid', message: tForm('messageRequired') };
    }

    if (audience.mode === 'platform') {
      const fields = {
        audienceSpec: audienceSpecFromState(audienceState),
        content: composeContentFromDraft(content, PLATFORM_EMAIL_SEND_VIA),
      };
      const input: CreateBroadcastInput =
        action.mode === 'schedule'
          ? { ...fields, mode: 'schedule', scheduledSendAt: action.scheduledIso }
          : { ...fields, mode: action.mode };
      // Committing a platform-wide send is step-up'd server-side (RBAC
      // Decision C). runStepUp opens Clerk's reverification modal on
      // `admin/requires_step_up` and retries once on success; it passes
      // through transparently for draft saves, which carry no step-up.
      let outcome: StepUpOutcome<unknown>;
      try {
        outcome = await runStepUp(() => createPlatform.mutateAsync(input));
      } catch (error) {
        // A Clerk reverification failure other than the member cancelling.
        return { status: 'failed', error };
      }
      if (outcome.status === 'cancelled') return { status: 'cancelled' };
      if (outcome.status === 'error') return { status: 'failed', error: outcome.error };
      return { status: 'done' };
    }

    // Group broadcasts are email; the Channel control is platform-only.
    const { email } = content;
    const preheaderValue = email.preheaderText.trim();
    const replyToValue = email.replyTo.trim();
    const fields = {
      kind: audience.kind,
      groupId: audience.groupId,
      subject: email.subject.trim(),
      bodyDoc: email.bodyDoc,
      ...(fromPost !== null ? { sourcePostId: fromPost.sourcePostId } : {}),
      ...(preheaderValue.length > 0 ? { preheaderText: preheaderValue } : {}),
      ...(replyToValue.length > 0 ? { replyTo: replyToValue } : {}),
    };
    const input: CreateGroupBroadcastInput =
      action.mode === 'schedule'
        ? { ...fields, mode: 'schedule', scheduledSendAt: action.scheduledIso }
        : { ...fields, mode: action.mode };
    try {
      await createGroup.mutateAsync(input);
    } catch (error) {
      return { status: 'failed', error };
    }
    return { status: 'done' };
  };

  // Copy for a non-completed outcome, or `null` when nothing should render
  // (the member dismissed step-up reverification).
  const failureCopy = (outcome: SubmitOutcome, fallback: string): string | null => {
    if (outcome.status === 'invalid') return outcome.message;
    if (outcome.status === 'failed') return describeError(outcome.error, fallback);
    return null;
  };

  const navigateAway = () => {
    guard.allowNextNavigation();
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  const handleSendNow = async () => {
    const outcome = await submit({ mode: 'send' });
    if (outcome.status === 'done') return navigateAway();
    setError(failureCopy(outcome, tForm('sendFailed')));
  };

  const handleSchedule = async (iso: string) => {
    const outcome = await submit({ mode: 'schedule', scheduledIso: iso });
    if (outcome.status === 'done') return navigateAway();
    setError(failureCopy(outcome, tForm('scheduleFailed')));
  };

  const handleGuardSave = async () => {
    setDialogError(null);
    setSavePending(true);
    const outcome = await submit({ mode: 'draft' });
    setSavePending(false);
    if (outcome.status === 'done') return guard.proceed();
    setDialogError(failureCopy(outcome, tForm('saveFailed')));
  };

  const handleCancel = () => {
    if (audience.mode === 'platform') {
      void navigate({ to: '/admin/broadcasts' });
    } else {
      window.history.back();
    }
  };

  return (
    <Stack gap="lg">
      <BroadcastEditorFields
        audience={audience}
        content={content}
        onContentChange={setContent}
        audienceState={audienceState}
        onAudienceStateChange={setAudienceState}
        disabled={pending}
      />

      {error !== null ? (
        <Body size="sm" color="destructive">
          {error}
        </Body>
      ) : null}

      <Row gap="md" justify="flex-end" align="center">
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <ScheduleSendButton
          disabled={!minimumFieldsForSave}
          pending={pending}
          onSendNow={handleSendNow}
          onSchedule={handleSchedule}
        />
      </Row>

      <UnsavedChangesDialog
        open={guard.dialogOpen}
        onCancel={guard.reset}
        onSave={() => void handleGuardSave()}
        onDiscard={guard.proceed}
        saveDisabled={!minimumFieldsForSave}
        saveDisabledReason={
          minimumFieldsForSave
            ? undefined
            : content.channel === 'dm'
              ? tDm('saveDisabledReason')
              : tForm('draftNeedsContent')
        }
        savePending={savePending}
        error={dialogError}
      />
    </Stack>
  );
}

